<template>
  <v-container class="pa-4" fluid>
    <!-- Cargando -->
    <div v-if="loading" class="d-flex flex-column align-center justify-center pa-8">
      <v-progress-circular indeterminate color="primary" size="60" class="mb-4"></v-progress-circular>
      <p class="text-center subtitle-1 grey--text">Cargando solicitud de autorización...</p>
    </div>

    <!-- Contenido Principal -->
    <div v-else-if="data">
      <!-- Toolbar de la vista -->
      <v-toolbar flat color="primary" dark class="mb-4 rounded-lg" elevation="2">
        <v-icon class="mr-3">assignment_turned_in</v-icon>
        <v-toolbar-title class="headline font-weight-bold">Autorización de Acceso - Embarque</v-toolbar-title>
        <v-spacer></v-spacer>
        <v-chip color="white" text-color="primary" label class="font-weight-bold">
          <v-icon small left>access_time</v-icon>
          {{ data.arrival_time || 'N/A' }}
        </v-chip>
      </v-toolbar>

      <!-- Estado si ya fue autorizado -->
      <v-alert v-if="data.is_authorized" type="success" icon="check_circle" class="mb-4" prominent>
        <strong>Acceso Autorizado</strong>
        <div class="mt-2 ml-4">
          <small><strong>Autorizado por:</strong> {{ data.authorized_by || 'N/A' }}</small><br>
          <small><strong>Fecha:</strong> {{ data.authorized_at || 'N/A' }}</small>
        </div>
      </v-alert>

      <!-- Contador de intentos -->
      <v-card class="mb-4" flat>
        <v-card-text class="pa-3">
          <div class="d-flex flex-wrap align-center justify-space-between">
            <div class="d-flex align-center">
              <v-icon color="warning" class="mr-2">info</v-icon>
              <span class="subtitle-2">Intento de Autorización</span>
            </div>
            <v-progress-linear
              :value="(data.authorization_attempts / 2) * 100"
              :color="data.authorization_attempts >= 2 ? 'error' : 'warning'"
              style="max-width: 200px; flex: 1; min-width: 150px;"
              class="ml-4 mr-4"
            ></v-progress-linear>
            <span class="font-weight-bold">{{ data.authorization_attempts }}/2</span>
          </div>
        </v-card-text>
      </v-card>

      <!-- Grid de información principal -->
      <v-layout row wrap class="mb-4">
        <!-- Información del Operador -->
        <v-flex xs12 sm6 md4 class="pa-2">
          <v-card elevation="2" class="h-100">
            <v-card-title class="primary--text pb-0">
              <v-icon small left color="primary">account_circle</v-icon>
              Operador
            </v-card-title>
            <v-divider class="mt-2"></v-divider>
            <v-card-text class="pa-4">
              <div class="info-field mb-3">
                <p class="grey--text text--darken-1 mb-1" style="font-size: 11px; text-transform: uppercase;">Nombre</p>
                <p class="subtitle-1 primary--text font-weight-bold">{{ data.operator.name || 'N/A' }}</p>
              </div>
              <div class="info-field mb-3">
                <p class="grey--text text--darken-1 mb-1" style="font-size: 11px; text-transform: uppercase;">Email</p>
                <p class="caption">{{ data.operator.email || 'N/A' }}</p>
              </div>
              <div v-if="data.operator.phone" class="info-field">
                <p class="grey--text text--darken-1 mb-1" style="font-size: 11px; text-transform: uppercase;">Teléfono</p>
                <p class="caption">{{ data.operator.phone }}</p>
              </div>
            </v-card-text>
          </v-card>
        </v-flex>

        <!-- Información de Carga -->
        <v-flex xs12 sm6 md4 class="pa-2">
          <v-card elevation="2" class="h-100">
            <v-card-title class="primary--text pb-0">
              <v-icon small left color="primary">location_on</v-icon>
              Carga
            </v-card-title>
            <v-divider class="mt-2"></v-divider>
            <v-card-text class="pa-4">
              <div class="info-field mb-3">
                <p class="grey--text text--darken-1 mb-1" style="font-size: 11px; text-transform: uppercase;">Área</p>
                <p class="subtitle-1 primary--text font-weight-bold">{{ data.loading_area.name || 'N/A' }}</p>
                <p class="caption grey--text">{{ data.loading_area.type || 'N/A' }}</p>
              </div>
              <div class="info-field">
                <p class="grey--text text--darken-1 mb-1" style="font-size: 11px; text-transform: uppercase;">Ventana</p>
                <p class="caption font-weight-bold">{{ data.window_date || 'N/A' }}</p>
                <p class="caption">{{ data.window_time || 'N/A' }}</p>
              </div>
            </v-card-text>
          </v-card>
        </v-flex>

        <!-- Información del Vehículo -->
        <v-flex v-if="data.vehicle_info" xs12 sm6 md4 class="pa-2">
          <v-card elevation="2" class="h-100">
            <v-card-title class="primary--text pb-0">
              <v-icon small left color="primary">directions_car</v-icon>
              Vehículo
            </v-card-title>
            <v-divider class="mt-2"></v-divider>
            <v-card-text class="pa-4">
              <div class="info-field mb-3">
                <p class="grey--text text--darken-1 mb-1" style="font-size: 11px; text-transform: uppercase;">Placa</p>
                <p class="subtitle-1 primary--text font-weight-bold">{{ data.vehicle_info.license_plate || 'N/A' }}</p>
              </div>
              <div class="info-field mb-3">
                <p class="grey--text text--darken-1 mb-1" style="font-size: 11px; text-transform: uppercase;">Tipo</p>
                <p class="caption">{{ data.vehicle_info.type || 'N/A' }}</p>
              </div>
              <div class="info-field">
                <p class="grey--text text--darken-1 mb-1" style="font-size: 11px; text-transform: uppercase;">Capacidad</p>
                <p class="caption">{{ data.vehicle_info.capacity_tonnage || 'N/A' }} toneladas</p>
              </div>
            </v-card-text>
          </v-card>
        </v-flex>
      </v-layout>

      <!-- Plantas Destino - Full Width -->
      <v-card class="mb-4" elevation="2">
        <v-card-title class="primary--text pb-0">
          <v-icon small left color="primary">local_shipping</v-icon>
          Plantas Destino
          <v-chip small color="primary" text-color="white" class="ml-2">
            {{ data.destinations_count || 0 }}
          </v-chip>
        </v-card-title>
        <v-divider class="mt-2"></v-divider>
        <v-card-text>
          <!-- Vista simple para 1 destino -->
          <div v-if="data.destinations_count === 1 && data.destinations && data.destinations.length > 0">
            <div class="destination-single-container">
              <div class="destination-single pa-4 rounded" style="background: #f5f5f5; border-left: 4px solid #002b48;">
                <h3 class="primary--text mb-3">{{ data.destinations[0].name || 'N/A' }}</h3>
                <div class="ml-2">
                  <p><strong>Dirección:</strong> {{ data.destinations[0].address || 'N/A' }}</p>
                  <p><strong>Código:</strong> {{ data.destinations[0].code || 'N/A' }}</p>
                  <p class="primary--text font-weight-bold"><v-icon small>schedule</v-icon> Tiempo de carga: {{ data.destinations[0].loading_time_minutes || 0 }} min</p>
                </div>
              </div>
            </div>
          </div>

          <!-- Vista de columnas para múltiples destinos -->
          <div v-else-if="data.destinations && data.destinations.length > 0">
            <v-layout row wrap>
              <v-flex v-for="(dest, idx) in data.destinations" :key="dest.id || idx" xs12 sm6 md4 lg3 class="pa-2">
                <v-card outlined class="destination-card fill-height">
                  <v-card-text class="pa-4">
                    <div class="d-flex flex-column align-start fill-height">
                      <div class="dest-number primary white--text d-flex align-center justify-center mb-3" style="width: 45px; height: 45px; border-radius: 50%; flex-shrink: 0;">
                        #{{ idx + 1 }}
                      </div>
                      <div style="flex: 1; width: 100%;">
                        <h4 class="primary--text mb-1 subtitle-2 font-weight-bold">{{ dest.name || 'N/A' }}</h4>
                        <p class="caption grey--text mb-2">{{ dest.code || 'N/A' }}</p>
                        <p class="caption mb-3 text--secondary" style="word-break: break-word;">{{ dest.address || 'N/A' }}</p>
                        <v-chip small outlined color="primary" text-color="white">
                          <v-icon small left>schedule</v-icon>
                          {{ dest.loading_time_minutes || 0 }} min
                        </v-chip>
                      </div>
                    </div>
                  </v-card-text>
                </v-card>
              </v-flex>
            </v-layout>

            <!-- Resumen de tiempo -->
            <v-card class="mt-4" flat outlined>
              <v-card-text class="pa-4">
                <div class="d-flex flex-wrap align-center justify-space-between">
                  <strong>Tiempo total estimado:</strong>
                  <v-chip color="primary" text-color="white" class="ml-2">
                    <v-icon small left>schedule</v-icon>
                    {{ totalLoadingTime }} minutos
                  </v-chip>
                </div>
              </v-card-text>
            </v-card>
          </div>

          <div v-else class="pa-4 text-center">
            <p class="grey--text">No hay plantas destino disponibles</p>
          </div>
        </v-card-text>
      </v-card>

      <!-- Alerta si se alcanzó límite de intentos -->
      <v-alert v-if="data.authorization_attempts >= 2 && !data.is_authorized" type="error" icon="warning" class="mb-4" prominent outlined>
        Se ha alcanzado el límite de intentos (2/2). Por favor contacta a administración.
      </v-alert>

      <!-- Botones de Acción -->
      <v-card v-if="!data.is_authorized" class="mt-6 elevation-2">
        <v-card-text class="pa-4">
          <v-layout row wrap justify-center>
            <v-flex xs12 sm6 md4 class="pa-2">
              <v-btn
                color="success"
                large
                :loading="authorizing"
                :disabled="authorizing || data.authorization_attempts > 2"
                @click="handleAuthorize"
                block
                class="font-weight-bold"
              >
                <v-icon left>check_circle</v-icon>
                Autorizar
              </v-btn>
            </v-flex>
            <v-flex xs12 sm6 md4 class="pa-2">
              <v-btn
                color="grey"
                large
                :loading="authorizing"
                :disabled="authorizing"
                @click="$router.back()"
                block
                class="font-weight-bold"
              >
                <v-icon left>arrow_back</v-icon>
                Atrás
              </v-btn>
            </v-flex>
          </v-layout>
        </v-card-text>
      </v-card>
    </div>
  </v-container>
</template>

<script>
import '@/plugins/axios'
import store from '@/store'
import axios from 'axios'

export default {
  name: 'ShipmentAuthorizationRequest',
  data() {
    return {
      qr_code: '',
      data: null,
      loading: true,
      authorizing: false
    }
  },
  computed: {
    totalLoadingTime() {
      if (!this.data || !this.data.destinations) return 0
      return this.data.destinations.reduce((sum, dest) => sum + (dest.loading_time_minutes || 0), 0)
    }
  },
  created() {
    this.qr_code = this.$route.params.qr_code
    if (!this.qr_code) {
      this.showError('QR code no encontrado en la URL')
      this.loading = false
      return
    }
    this.fetchAuthorizationData()
  },
  methods: {
    fetchAuthorizationData() {
      this.loading = true
      const config = {
        method: 'get',
        url: `https://empresa-service.intrare.app/shipments/authorization-request/${this.qr_code}/`,
        headers: {
          'Content-Type': 'application/json',
          'Accept-Language': this.$store.state.language || 'es',
          'authorization': 'Token ' + store.getters.getToken
        }
      }

      axios(config)
        .then(response => {
          if (response && response.data) {
            this.data = this.normalizeData(response.data)
            console.log('Datos de autorización cargados:', this.data)
            this.loading = false
          } else {
            this.showError('Respuesta vacía del servidor')
            this.loading = false
          }
        })
        .catch(error => {
          let errorMsg = 'Error al cargar la solicitud de autorización'

          if (error.response) {
            if (error.response.status === 404) {
              errorMsg = 'Solicitud no encontrada. Verifica el código QR'
            } else if (error.response.status === 403) {
              errorMsg = 'No tienes permiso para ver esta solicitud'
            } else if (error.response.status === 401) {
              errorMsg = 'Sesión expirada. Por favor inicia sesión nuevamente'
            } else if (error.response.data && error.response.data.error) {
              errorMsg = error.response.data.error
            } else if (error.response.statusText) {
              errorMsg = error.response.statusText
            }
          } else if (error.message) {
            errorMsg = error.message
          }

          this.showError(errorMsg)
          this.loading = false
        })
    },

    normalizeData(data) {
      // Normalizar datos con fallbacks
      return {
        id: data.id || 'N/A',
        qr_code: data.qr_code || '',
        operator: {
          name: (data.operator && data.operator.name) || 'Sin nombre',
          email: (data.operator && data.operator.email) || 'Sin email',
          phone: (data.operator && data.operator.phone) || null
        },
        arrival_time: data.arrival_time || new Date().toLocaleString(),
        loading_area: {
          id: (data.loading_area && data.loading_area.id) || 'N/A',
          name: (data.loading_area && data.loading_area.name) || 'Área desconocida',
          type: (data.loading_area && data.loading_area.type) || 'N/A'
        },
        destinations: Array.isArray(data.destinations) ? data.destinations.map(d => ({
          id: d.id || Math.random(),
          name: d.name || 'Planta sin nombre',
          address: d.address || 'Dirección no disponible',
          loading_time_minutes: d.loading_time_minutes || 0,
          code: d.code || 'N/A'
        })) : [],
        destinations_count: data.destinations_count || (Array.isArray(data.destinations) ? data.destinations.length : 0),
        transport_type: data.transport_type || 'No especificado',
        window_date: data.window_date || 'N/A',
        window_time: data.window_time || 'N/A',
        is_internal: data.is_internal || false,
        vehicle_info: data.vehicle_info ? {
          id: data.vehicle_info.id || 'N/A',
          license_plate: data.vehicle_info.license_plate || 'Sin placa',
          type: data.vehicle_info.type || 'No especificado',
          capacity_tonnage: data.vehicle_info.capacity_tonnage || 0
        } : null,
        authorization_attempts: data.authorization_attempts || 0,
        is_authorized: data.is_authorized || false,
        authorized_at: data.authorized_at || null,
        authorized_by: data.authorized_by || null
      }
    },

    handleAuthorize() {
      if (!this.data || !this.qr_code) {
        this.showError('Datos incompletos para autorizar')
        return
      }

      this.authorizing = true

      const config = {
        method: 'post',
        url: 'https://empresa-service.intrare.app/shipments/supervisor/authorize-access/',
        headers: {
          'Content-Type': 'application/json',
          'Accept-Language': this.$store.state.language || 'es',
          'authorization': 'Token ' + store.getters.getToken
        },
        data: {
          qr_code: this.qr_code
        }
      }

      axios(config)
        .then(response => {
          this.showSuccess('Acceso autorizado exitosamente')

          // Actualizar estado
          if (this.data) {
            this.data.is_authorized = true
            this.data.authorized_at = new Date().toLocaleString()
            this.data.authorized_by = 'Tú'
          }

          // Redirigir después de 2 segundos
          setTimeout(() => {
            this.$router.push('/mockup3')
          }, 2000)
        })
        .catch(error => {
          let errorMsg = 'No se pudo autorizar el acceso'

          if (error.response) {
            if (error.response.status === 403) {
              errorMsg = 'No tienes permiso para autorizar este acceso'
            } else if (error.response.status === 401) {
              errorMsg = 'Sesión expirada. Por favor inicia sesión nuevamente'
            } else if (error.response.status === 400) {
              errorMsg = error.response.data && error.response.data.error ? error.response.data.error : 'Datos inválidos'
            } else if (error.response.data && error.response.data.error) {
              errorMsg = error.response.data.error
            } else if (error.response.statusText) {
              errorMsg = error.response.statusText
            }
          } else if (error.message) {
            errorMsg = error.message
          }

          this.showError(errorMsg)
        })
        .finally(() => {
          this.authorizing = false
        })
    },

    showSuccess(message) {
      this.$store.commit('snackbar_activate_true')
      this.$store.commit('snackbar_change_message', message)
      this.$store.commit('snackbar_change_color', 'success')
    },

    showError(message) {
      this.$store.commit('snackbar_activate_true')
      this.$store.commit('snackbar_change_message', message)
      this.$store.commit('snackbar_change_color', 'error')
    }
  }
}
</script>

<style scoped>
.destination-single-container {
  margin-top: 12px;
}

.destination-single {
  transition: all 0.3s ease;
}

.destination-single:hover {
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.12);
}

.destination-card {
  transition: all 0.3s ease;
  height: 100%;
  display: flex;
  flex-direction: column;
}

.destination-card:hover {
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
  transform: translateY(-2px);
}

.dest-number {
  background: linear-gradient(135deg, #002b48 0%, #003d63 100%);
  font-weight: bold;
  font-size: 18px;
  flex-shrink: 0;
}

.rounded-lg {
  border-radius: 8px;
}

.fill-height {
  display: flex;
  flex-direction: column;
  height: 100%;
}

.info-field {
  word-break: break-word;
  overflow-wrap: break-word;
}

/* Responsive adjustments */
@media (max-width: 600px) {
  .destination-card {
    margin-bottom: 8px;
  }

  .rounded-lg {
    border-radius: 4px;
  }

  .v-toolbar__title {
    font-size: 18px !important;
  }
}

@media (min-width: 601px) and (max-width: 960px) {
  .destination-card {
    margin-bottom: 12px;
  }
}

@media (min-width: 961px) {
  .pa-2 {
    padding: 8px !important;
  }
}
</style>
